import styled from '@emotion/styled'
import { MediaQuery } from '@pharmac-extranet/shared'
import { PharmacIcon } from '@pharmac/ui'
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useSwipeable } from 'react-swipeable'

const CarouselContainer = styled.div`
  width: 100%;

  .encarousel__wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    position: relative;
    width: 100%;
    overflow: hidden;

    .encarousel__sidebar {
      width: 30px;
      height: 30px;
      z-index: 2;

      ${MediaQuery.maxMobile} {
        display: none;
      }

      button {
        background-color: var(--color-purple-light);
        width: 30px;
        height: 30px;
        border-radius: 50%;

        transition: var(--transition-cubic-bezier);

        box-shadow: var(--button-primary-box-shadow);

        &:hover {
          transform: var(--button-primary-transform-hover);
          box-shadow: var(--button-primary-box-shadow-hover);
        }

        svg {
          width: 26px;
          height: 26px;
          color: var(--color-white);
        }
      }

      &--left {
        position: absolute;
        left: var(--spacing-small);

        svg {
          margin-right: 2px;
          margin-top: 2px;
        }
      }

      &--right {
        position: absolute;
        right: var(--spacing-small);

        svg {
          margin-left: 3px;
          margin-top: 2px;
        }
      }
    }

    .encarousel__images {
      width: 100%;
      white-space: nowrap;
      transition: transform 0.3s;
    }

    .encarousel__dots {
      position: absolute;
      bottom: 20px;
      left: 50%;

      transform: translateX(-50%);

      display: flex;
      flex-direction: row;

      .encarousel__dot {
        height: 10px;
        width: 10px;
        background-color: var(--color-white);
        border-radius: 50%;

        transition: background-color 0.3s;

        &--active {
          background-color: var(--color-purple);
        }

        :not(:first-of-type) {
          margin-left: var(--spacing-small);
        }
      }
    }
  }
`
const CarouselImage = styled.img`
  width: auto;
  height: auto;
  object-fit: cover;
  border-radius: var(--border-radius-small);

  ${MediaQuery.minTablet} {
    max-width: 75%;
  }

  ${MediaQuery.maxTablet} {
    border-radius: 0;
  }

  max-width: 100% !important;

  cursor: pointer;
`

const CarouselItem = styled.div`
  display: inline-flex;
  width: 100%;
`

type ExtranetCarouselItemProps = PropsWithChildren

export const ExtranetCarouselItem = ({ children }: ExtranetCarouselItemProps) => {
  return <CarouselItem className="encarousel__item">{children}</CarouselItem>
}

type ExtranetCarouselImageProps = React.ComponentProps<'img'> & {
  imageSrc: string
  imageAlt?: string
}

export const ExtranetCarouselImage = ({
  imageSrc,
  imageAlt,
  ...props
}: ExtranetCarouselImageProps) => {
  return (
    <CarouselImage
      className="encarousel__image"
      src={imageSrc}
      alt={imageAlt}
      {...props}
    />
  )
}

type ExtranetCarouselProps = PropsWithChildren &
  React.ComponentProps<'div'> & {
    autoplay?: boolean
    autoplayInterval?: number
  }

export const ExtranetCarousel = ({
  children,
  autoplay,
  autoplayInterval = 10000,
  ...props
}: ExtranetCarouselProps) => {
  const childrenCount = React.Children.count(children)

  const imageCountRef = useRef<null | number>(null)

  useEffect(() => {
    imageCountRef.current = childrenCount
  }, [childrenCount])

  const [activeIndex, setActiveIndex] = useState(0)

  const updateIndex = (direction: number) =>
    setActiveIndex((currentIndex) => {
      if (imageCountRef.current === null) return currentIndex

      const maxIndex = imageCountRef.current - 1

      if (direction < 0 && currentIndex === 0) return maxIndex
      if (direction > 0 && currentIndex === maxIndex) return 0

      return currentIndex + direction
    })
  const changeSlide = (direction: number) => {
    if (direction !== 1 && direction !== -1) return

    if (autoplayIntervalRef.current) {
      clearInterval(autoplayIntervalRef.current)

      autoplayIntervalRef.current = setInterval(() => {
        updateIndex(direction)
      }, autoplayInterval)
    }
    updateIndex(direction)
  }

  const wrapperStyle = {
    transform: `translateX(-${activeIndex * 100}%)`,
  }

  const autoplayIntervalRef = useRef<null | NodeJS.Timeout>()

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => changeSlide(1),
    onSwipedRight: () => changeSlide(-1),
  })

  useEffect(() => {
    if (autoplay && autoplayInterval) {
      if (autoplayIntervalRef.current) {
        clearInterval(autoplayIntervalRef.current)
        autoplayIntervalRef.current = null
      }

      autoplayIntervalRef.current = setInterval(() => {
        updateIndex(1)
      }, autoplayInterval)
    }
  }, [autoplay])

  return (
    <CarouselContainer
      className="encarousel"
      {...props}
    >
      <div className="encarousel__wrapper">
        {Array.from(Array(imageCountRef.current).keys()).length > 1 && (
          <div className="encarousel__sidebar encarousel__sidebar--left">
            <button
              className="encarouse__button encarousel__button--left"
              onClick={() => changeSlide(-1)}
            >
              <PharmacIcon icon="chevronLeft" />
            </button>
          </div>
        )}

        <div
          {...swipeHandlers}
          className="encarousel__images"
          style={wrapperStyle}
        >
          {children}
        </div>
        {Array.from(Array(imageCountRef.current).keys()).length > 1 && (
          <div className="encarousel__sidebar encarousel__sidebar--right">
            <button
              className="encarousel__button encarousel__button--right"
              onClick={() => changeSlide(1)}
            >
              <PharmacIcon icon="chevronRight" />
            </button>
          </div>
        )}
        {Array.from(Array(imageCountRef.current).keys()).length > 1 && (
          <div className="encarousel__dots">
            {Array.from(Array(imageCountRef.current).keys()).map((_, index) => (
              <button
                key={'encarousel-slide-btn-' + index}
                onClick={() => setActiveIndex(index)}
                className={['encarousel__dot', index === activeIndex && 'encarousel__dot--active']
                  .filter(Boolean)
                  .join(' ')}
              />
            ))}
          </div>
        )}
      </div>
    </CarouselContainer>
  )
}
